import {
  ArrowRightLeft,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  MoveRight,
  OctagonX,
  TrendingUp,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-preact'
import {
  SENDING_DOMAIN_PILL_LABELS,
  sendingDomainPill,
  type SendingDomainForecastDomainDto,
  type SendingDomainForecastPlanDto,
  type SendingDomainForecastViewDto,
  type SendingDomainPill,
  type SendingDomainRateLimitsDto,
} from '@/types/sendingDomains'
import { formatCount } from '@/utils/sendingDomainWarmup'
import {
  domainRuleLine,
  domainStateDetail,
  refusalHeadline,
  spreadDetail,
  spreadHeadline,
  spreadLegendDays,
  timedSendRefusal,
} from '../sendingDomainForecastText'

// The activation editor's notes under the sending-domain select (ORB-953): the domain's state, and how it
// would carry this email. Every state is a word and an icon as well as a colour: green Ready, blue
// Warming, amber for what needs attention, red Stopped.
const PILL_STYLES: Record<SendingDomainPill, { className: string; Icon: LucideIcon }> = {
  SettingUp: { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', Icon: CircleDashed },
  Warming: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', Icon: TrendingUp },
  Holding: { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', Icon: MoveRight },
  Ready: { className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', Icon: CircleCheck },
  Stopped: { className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', Icon: OctagonX },
}

export function SendingDomainStatePill({ pill }: { pill: SendingDomainPill }) {
  const { className, Icon } = PILL_STYLES[pill]
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {SENDING_DOMAIN_PILL_LABELS[pill]}
    </span>
  )
}

interface SendingDomainStateLinesProps {
  id: string
  domain: SendingDomainForecastDomainDto
  lastStep: number
  rateLimits: SendingDomainRateLimitsDto
}

/** The domain's pill and what it can send, then how it moves up, or why it is holding. */
export function SendingDomainStateLines({ id, domain, lastStep, rateLimits }: SendingDomainStateLinesProps) {
  const pill = sendingDomainPill(domain)
  if (!pill) return null
  const detail = domainStateDetail(pill, domain, lastStep)
  const rule = domainRuleLine(pill, domain, rateLimits)
  return (
    <div id={id} className="space-y-1 pt-0.5">
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <SendingDomainStatePill pill={pill} />{' '}
        {detail}
      </p>
      {rule && <p className="text-xs text-slate-500 dark:text-slate-400">{rule}</p>}
    </div>
  )
}

function SwitchDomainButton({ domain, onSwitch }: { domain: SendingDomainForecastDomainDto; onSwitch: (domainId: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onSwitch(domain.Id)}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
      Switch to {domain.Domain}
    </button>
  )
}

interface SendingDomainRefusalBannerProps {
  id: string
  forecast: SendingDomainForecastViewDto
  /** A Ready domain to offer instead, when the company has one. */
  switchTo: SendingDomainForecastDomainDto | null
  onSwitch: (domainId: string) => void
}

/** Why Save would refuse this domain, before Save is pressed. */
export function SendingDomainRefusalBanner({ id, forecast, switchTo, onSwitch }: SendingDomainRefusalBannerProps) {
  // A timed send gets the times Save's own message leaves out; every other refusal reads as Save words it.
  const reason = timedSendRefusal(forecast) ?? forecast.RefusalReason
  const needsReadyDomain = forecast.RefusalCause === 'OverTodaysLimit'
  return (
    <div
      id={id}
      role="alert"
      className="flex gap-2.5 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-4">
        <div className="space-y-1">
          <p className="text-sm font-semibold">{refusalHeadline(forecast)}</p>
          {reason && <p className="text-[13px] leading-5">{reason}</p>}
        </div>
        {switchTo ? (
          <div className="space-y-2">
            <p className="text-[13px] leading-5">
              <strong className="font-semibold">Choose a Ready domain.</strong> {switchTo.Domain} is Ready and has no limits.
            </p>
            <SwitchDomainButton domain={switchTo} onSwitch={onSwitch} />
          </div>
        ) : needsReadyDomain && (
          <p className="text-[13px] leading-5">
            <strong className="font-semibold">Choose a Ready domain.</strong> None of this company's sending domains is Ready yet.
          </p>
        )}
      </div>
    </div>
  )
}

interface SendingDomainSpreadBannerProps {
  id: string
  forecast: SendingDomainForecastViewDto
  plan: SendingDomainForecastPlanDto
  switchTo: SendingDomainForecastDomainDto | null
  onSwitch: (domainId: string) => void
}

/** A broadcast the warm-up spreads across days: how many go each day, and how many miss the event. */
export function SendingDomainSpreadBanner({ id, forecast, plan, switchTo, onSwitch }: SendingDomainSpreadBannerProps) {
  const days = spreadLegendDays(plan)
  const detail = spreadDetail(forecast, plan)
  const missed = plan.AfterDeadline
  return (
    <div
      id={id}
      role="status"
      className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{spreadHeadline(plan)}</p>
          {detail && <p className="text-[13px] leading-5">{detail}</p>}
        </div>

        <div className="space-y-2">
          {/* The legend below carries every number, so the bar is for the eye only. */}
          <div className="flex h-2 gap-0.5 overflow-hidden rounded-full" aria-hidden="true">
            {days.map(day => (
              <div key={day.dayStartUtc} className="min-w-0.5 bg-sky-500" style={{ flexGrow: day.expected, flexBasis: 0 }} />
            ))}
            {missed > 0 && <div className="min-w-0.5 bg-hatched" style={{ flexGrow: missed, flexBasis: 0 }} />}
          </div>
          <div className="space-y-1 text-xs leading-5">
            {days.length > 0 && (
              <div className="flex items-start gap-1.5">
                <i className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-sm bg-sky-500" aria-hidden="true" />
                <span>
                  {days.map((day, index) => (
                    <span key={day.dayStartUtc}>
                      <span className="whitespace-nowrap">
                        {day.label}{' '}
                        <span className="font-semibold tabular-nums text-amber-900 dark:text-amber-100">{formatCount(day.expected)}</span>
                        {index < days.length - 1 && ' ·'}
                      </span>{' '}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {missed > 0 && (
              <div className="flex items-start gap-1.5">
                <i className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-sm bg-hatched" aria-hidden="true" />
                <span>
                  <span className="font-semibold tabular-nums text-amber-900 dark:text-amber-100">{formatCount(missed)}</span> after the start, not sent
                </span>
              </div>
            )}
          </div>
        </div>

        {missed > 0 && switchTo && (
          <div className="space-y-2">
            <p className="text-[13px] leading-5">
              <strong className="font-semibold text-amber-900 dark:text-amber-200">To reach everyone,</strong> send it from a Ready domain.
            </p>
            <SwitchDomainButton domain={switchTo} onSwitch={onSwitch} />
          </div>
        )}
      </div>
    </div>
  )
}

/** Registrant emails share a warming domain's day but are never held. */
export function RegistrantEmailsNote({ besideEstimate }: { besideEstimate: boolean }) {
  return (
    <p className="text-xs text-slate-500 dark:text-slate-400">
      Emails to people who registered are never held: confirmations, reminders and post-event follow-ups always send
      straight away and count toward the day's limit{besideEstimate ? ', so this estimate can drop.' : '.'}
    </p>
  )
}
